/**
 * Aufrufe der Berichte und der Startseite -- F5-05 bis F5-10.
 *
 * **Der Berichtskatalog kommt vom Server.** Die Oberflaeche baut ihre Auswahl
 * aus `/reports` und fuehrt keine eigene Liste: Eine zweite Liste liefe
 * auseinander, sobald ein Bericht dazukommt -- und der Anwender saehe eine
 * Achse, die der Server nicht kennt.
 */

import type { components } from '../../api/schema';
import { download, request } from '../../lib/apiClient';

export type ReportCatalogEntry = components['schemas']['ReportCatalogEntry'];
export type Report = components['schemas']['ReportRead'];
export type ReportRow = components['schemas']['ReportRow'];
export type Dashboard = components['schemas']['DashboardRead'];
export type DashboardTile = components['schemas']['DashboardTileRead'];
export type ReportSchedule = components['schemas']['ReportScheduleRead'];

export interface ReportFilters {
  since?: string;
  until?: string;
  customer_id?: string;
  queue_id?: string;
}

export function listReports(): Promise<ReportCatalogEntry[]> {
  return request<ReportCatalogEntry[]>('/reports');
}

export function runReport(
  key: string,
  axis: string | undefined,
  filters: ReportFilters,
): Promise<Report> {
  return request<Report>(`/reports/${key}`, {
    query: { ...filters, ...(axis ? { axis } : {}) },
  });
}

export function fetchDashboard(): Promise<Dashboard> {
  return request<Dashboard>('/dashboard');
}

export function listTiles(): Promise<DashboardTile[]> {
  return request<DashboardTile[]>('/dashboard/tiles');
}

export function createTile(payload: Record<string, unknown>): Promise<DashboardTile> {
  return request<DashboardTile>('/dashboard/tiles', { method: 'POST', body: payload });
}

export function deleteTile(tileId: string): Promise<undefined> {
  return request<undefined>(`/dashboard/tiles/${tileId}`, { method: 'DELETE' });
}

export type SurveySummary = components['schemas']['SurveySummary'];

/** Wo die Umfragen stehen: wartend, verschickt, beantwortet -- F5-10. */
export function getSurveySummary(): Promise<SurveySummary> {
  return request<SurveySummary>('/surveys/summary');
}

/**
 * Verschickt alle wartenden Umfragen sofort -- F5-10.
 *
 * Auch die, deren Verzoegerung noch laeuft. Der Hintergrunddienst haelt sie
 * ein; wer den Knopf drueckt, will ausdruecklich nicht warten. Zuvor rief der
 * Knopf denselben Lauf wie der Dienst und verschickte am Tag des Abschlusses
 * deshalb nichts.
 */
export function sendSurveysNow(): Promise<{ sent: number }> {
  return request<{ sent: number }>('/surveys/actions/send-now', { method: 'POST' });
}

/**
 * Holt einen Bericht als Datei.
 *
 * Ueber `fetch` und nicht ueber einen Link: Ein `<a href>` traegt den
 * `Authorization`-Kopf nicht mit, und ein Token in der Adresszeile stuende im
 * Verlauf des Browsers -- dieselbe Ueberlegung wie beim Ticketausdruck.
 */
export function downloadReport(
  key: string,
  axis: string | undefined,
  filters: ReportFilters,
  format: 'csv' | 'pdf',
): Promise<{ blob: Blob; filename: string | null }> {
  const params = new URLSearchParams();
  if (axis) params.set('axis', axis);
  for (const [name, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') params.set(name, String(value));
  }
  const search = params.toString();
  return download(`/reports/${key}/export.${format}${search ? `?${search}` : ''}`);
}

/**
 * Geplanter Berichtsversand -- F5-09, K-125.
 *
 * Lesen darf, wer Berichte liest; einrichten nur, wer den Mandanten
 * einrichtet. Wer festlegt, welche Auswertung an welche Adressen geht, trifft
 * keine Leseentscheidung.
 */
export function listReportSchedules(): Promise<ReportSchedule[]> {
  return request<ReportSchedule[]>('/report-schedules');
}

export function createReportSchedule(payload: Record<string, unknown>): Promise<ReportSchedule> {
  return request<ReportSchedule>('/report-schedules', { method: 'POST', body: payload });
}

export function deleteReportSchedule(scheduleId: string): Promise<undefined> {
  return request<undefined>(`/report-schedules/${scheduleId}`, { method: 'DELETE' });
}
